<script lang="ts">
  import { Howl } from 'howler';
  import { CurrentTrack, PlayState, STATE, type Track } from './RecordStore';
  import Mute from './ui/mute.svg?component';
  import Play from './ui/play.svg?component';
  import Unmute from './ui/unmute.svg?component';
  import Buffering from './ui/buffering.svelte';
  import TidalLogo from './ui/TidalLogo.svg?component';
  import { fade } from 'svelte/transition';
  import { onDestroy } from 'svelte';

  export let trackList: Track[];
  let trackNum = Math.floor(Math.random() * trackList.length);
  $CurrentTrack = trackList[trackNum];

  let sound: Howl;
  let icon: typeof Play | typeof Buffering = Play;
  let initialLoad = true;
  let shouldPlay = false;
  let showToolTip = false;
  const toolTip = 'Full playlist on TIDAL';
  const cta1 = 'Block Vibes';
  const cta2 = 'Curated by JAY-Z';

  //create a new howl instance
  const initPlayer = (fileName: string) => {
    if (sound) {
      sound.stop();
      sound.off();
      sound.unload();
    }
    sound = new Howl({
      src: [`/playlist/${fileName}`],
      pool: 20,
      html5: true,
      preload: 'metadata'
    });
    sound.once('load', () => {
      PlayState.set(STATE.READY);

      if (shouldPlay) {
        sound.play();
      }
    });
    sound.on('play', () => PlayState.set(STATE.PLAYING));
    sound.on('pause', () => {
      PlayState.set(STATE.PAUSED);
    });
    sound.once('end', () => {
      PlayState.set(STATE.COMPLETED);
      loadNextTrack();
    });
    PlayState.set(STATE.BUFFERING);
  };

  //load new track when track changes
  $: if ($CurrentTrack) {
    initPlayer($CurrentTrack.fileName);
  }

  //update icon when play state changes
  $: {
    switch ($PlayState) {
      case STATE.PLAYING:
        icon = Unmute;
        break;
      case STATE.PAUSED:
        icon = Mute;
        break;
      case STATE.UNINITIALIZED:
      case STATE.BUFFERING:
      case STATE.COMPLETED:
        icon = Buffering;
        break;
      case STATE.READY:
        icon = Play;
        break;
    }
  }

  const handlePlayPause = () => {
    shouldPlay = !shouldPlay;
    if (sound) {
      if (shouldPlay) {
        if (!sound.playing()) {
          sound.play();
        }
      } else {
        if (sound.playing()) {
          sound.pause();
        }
      }
    }
    initialLoad = false;
  };

  const loadNextTrack = () => {
    trackNum++;
    if (trackNum >= trackList.length) {
      trackNum = 0;
    }
    CurrentTrack.set(trackList[trackNum]);
  };

  //carry over from react, I don't remember what bug this was solving, maybe something with mobile?
  const blur = () => {
    const links = document.getElementsByTagName('a');
    for (var link of links) {
      link.blur();
    }
    showToolTip = false;
  };

  //skip track handler
  const keypress = (e: KeyboardEvent) => {
    //keyCode is deprecated?? why?
    if (e.key == 'ArrowRight') {
      loadNextTrack();
    }
  };

  onDestroy(() => {
    sound.stop();
  });
</script>

<svelte:window on:keyup={keypress} />

<div class="playbackControls">
  <button
    type="button"
    class="left btn"
    on:click={handlePlayPause}
    tabIndex={0}
    aria-label="Play Block Vibes Curated by JAY-Z"
  >
    <div class="iconWrapper">
      {#key icon}
        <div class="icon" in:fade={{ duration: 200, delay: 200 }} out:fade={{ duration: 200 }}>
          <svelte:component this={icon} />
        </div>
      {/key}
    </div>

    <div class="trackInfo">
      <div class="title">{showToolTip ? toolTip : initialLoad ? cta1 : $CurrentTrack.title}</div>
      <div class="artist">{showToolTip || initialLoad ? cta2 : $CurrentTrack.artist}</div>
    </div>
  </button>
  <a
    on:click={blur}
    on:mouseenter={() => (showToolTip = true)}
    on:mouseleave={() => (showToolTip = false)}
    class="right btn"
    href="https://tidal.com/browse/playlist/3d95c4f6-dad5-4d7f-a469-8bde01b7771d"
    rel="noopener noreferrer"
    target="_blank"
    tabIndex={0}
    aria-label={`Open Block Vibes Curated by JAY-Z in TIDAL`}
  >
    <TidalLogo />
  </a>
</div>

<style lang="scss">
  .playbackControls {
    @import 'src/scss/variables';
    @import 'src/scss/media';
    $buttonFade: 0.2s ease-out;
    $clearWhite: rgba(255, 255, 255, 0);

    display: grid;
    grid-template-columns: auto min-content;
    height: 56px;
    white-space: nowrap;
    width: 100%;

    overflow: hidden;
    border-radius: 999px;
    border: 1px var(--twist-color-gray-100) solid;

    .iconWrapper {
      position: relative;
      display: inline-block;
      width: 24px;
      height: 24px;
      overflow: hidden;
    }
    .icon {
      display: inline;
      vertical-align: top;
      position: absolute;
      top: 0;
      left: 0;
    }

    .btn {
      display: grid;
      background: $clearWhite;
      color: var(--twist-color-gray-100);
      position: relative;
      height: 56px;
      border: none;
      pointer-events: all;
      cursor: pointer;
      transition:
        background $buttonFade,
        color $buttonFade;
      user-select: none;

      &:active {
        background: var(--twist-color-gray-40);
      }
      &:focus {
        background: var(--twist-color-gray-0);
        z-index: 1;
      }
      &:focus:not(:focus-visible) {
        background: transparent;
      }
      &:focus-visible {
        background: var(--twist-color-gray-0);
        z-index: 1;
        outline: none;
      }
    }

    .left {
      padding: 15px 12px 17px 20px;
      min-width: 230px !important;
      display: flex;
      align-items: center;
      text-align: left;
    }
    .right {
      border-left: 1px var(--twist-color-gray-100) solid;
      padding: 16px 20px 16px 14px;
    }

    .trackInfo {
      margin-left: 12px;
      margin-right: 12px;
      height: 24px;
      overflow: hidden;
    }

    .title,
    .artist {
      font-family: var(--font-family-primary);
      font-size: 10px;
      font-style: normal;
      letter-spacing: 0.04em;
      line-height: 1.15;
      position: relative;
      text-overflow: ellipsis;
    }

    .title {
      font-weight: bold;
      margin-bottom: 1px;
    }
    .artist {
      font-weight: normal;
    }
  }

  @media (hover: hover) {
    .btn:hover {
      background: var(--twist-color-gray-0) !important;
      z-index: 1;
    }
  }
</style>
